import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button, Popover, Typography } from "stories/components";
import usePermissions from "hooks/usePermissions";

let sequence = 0;

/**
 * A button for an action that changes account data.
 *
 * A greyed-out button with no explanation is a dead end: the user cannot tell
 * whether it is loading, broken, or forbidden. This disables the action for a
 * read-only user and says why, on hover and to a screen reader.
 *
 * The popover hangs on a wrapping span rather than the button, because a disabled
 * element receives no mouse events.
 */
const WriteButton = ({ children, disabled, id, ...rest }) => {
  const { canWrite } = usePermissions();
  const { t } = useTranslation();

  const generatedId = useMemo(() => id || `write-action-${(sequence += 1)}`, [id]);
  const blocked = !canWrite;
  const reasonId = `${generatedId}-reason`;
  const reason = t("permission_write_required", "Vaatii muokkausoikeuden. Sinulla on lukuoikeus tähän tiliin.");

  const button = (
    <Button
      {...rest}
      id={generatedId}
      disabled={disabled || blocked}
      aria-describedby={blocked ? reasonId : undefined}
    >
      {children}
    </Button>
  );

  if (!blocked) {
    return button;
  }

  return (
    <>
      <span id={`${generatedId}-wrap`} style={{ display: "inline-block" }}>
        {button}
      </span>
      <span id={reasonId} className="sr-only">{reason}</span>
      <Popover placement="top" target={`${generatedId}-wrap`}>
        <Typography className="text-dark mb-1" variant="small">{reason}</Typography>
      </Popover>
    </>
  );
};

export default WriteButton;
